import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Select, MenuItem, Paper } from '@mui/material';
import DeletePopup from './delete';

export type ReportingType = 'quarter' | 'annual' | 'month' | '';

type Props = {
    open: boolean;
    anchorEl: HTMLElement | null;
    cycles: Record<number, ReportingType>;
    years: number[];
    activeYear?: number;
    onClose: () => void;
    onSave: (cycles: Record<number, ReportingType>) => void;
    onYearClick: (year: number) => void;
};

const REPORTING_OPTIONS: { label: string; value: ReportingType }[] = [
    { label: 'Select', value: '' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Annual', value: 'annual' },
    { label: 'Month', value: 'month' },
];

const GSTCycleModal: React.FC<Props> = ({ open, anchorEl, cycles, years, activeYear, onClose, onSave, onYearClick }) => {
    const [local, setLocal] = useState<Record<number, ReportingType>>({});
    // Years confirmed via the "Confirm GST Reporting Cycle" dialog during this
    // modal session — locked from further edits even before Save is clicked.
    const [confirmedYears, setConfirmedYears] = useState<Set<number>>(new Set());
    const [pendingSelection, setPendingSelection] = useState<{ year: number; value: ReportingType } | null>(null);

    useEffect(() => {
        if (open) {
            setLocal({ ...cycles });
            setConfirmedYears(new Set());
            setPendingSelection(null);
        }
    }, [open, cycles]);

    if (!open || !anchorEl) return null;

    const rect = anchorEl.getBoundingClientRect();
    const hasAnyEditableYear = years.some(year => !cycles[year]);
    // Anchored at the button's left edge normally, but clamped so a button
    // sitting near the right edge of a narrow (phone) viewport doesn't push
    // this fixed-260px-wide popup partly or fully off-screen.
    const popupWidth = 260;
    const viewportMargin = 16;
    const clampedLeft = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - popupWidth - viewportMargin));

    return (
        <>
            {/* Backdrop — click anywhere outside the popup to close */}
            <Box
                onClick={onClose}
                sx={{ position: 'fixed', inset: 0, zIndex: 1399 }}
            />
            <Paper
                elevation={4}
                sx={{
                    position: 'fixed',
                    top: rect.bottom + 6,
                    left: clampedLeft,
                    zIndex: 1400,
                    width: popupWidth,
                    maxWidth: `calc(100vw - ${viewportMargin * 2}px)`,
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Typography fontWeight={600} fontSize={14} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    GST Reporting Cycle
                </Typography>

                {/* Header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: 'var(--color-primary)' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', px: 2, py: 0.75 }}>YEAR</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', px: 2, py: 0.75 }}>REPORTING</Typography>
                </Box>

                {/* Rows — a year locks individually once it already has a saved value,
                    or once its cycle has been confirmed during this session;
                    years without either stay editable */}
                <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                    {years.map(year => {
                        const isLocked = !!cycles[year] || confirmedYears.has(year);
                        const isActive = year === activeYear;
                        return (
                            <Box
                                key={year}
                                onClick={() => onYearClick(year)}
                                sx={{
                                    display: 'grid', gridTemplateColumns: '1fr 1fr',
                                    alignItems: 'center',
                                    borderBottom: '1px solid #f0f0f0',
                                    px: 2, py: 0.5,
                                    cursor: 'pointer',
                                    background: isActive ? 'rgba(var(--color-primary-rgb), 0.0784)' : 'transparent',
                                    '&:hover': { background: isActive ? 'rgba(var(--color-primary-rgb), 0.1216)' : 'var(--color-container-background)' },
                                }}
                            >
                                <Typography
                                    fontSize={13}
                                    fontWeight={isActive ? 700 : 500}
                                    sx={{ color: isActive ? 'var(--color-primary)' : 'inherit' }}
                                >
                                    {year}
                                </Typography>
                                <Select
                                    size="small"
                                    value={local[year] ?? ''}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => {
                                        const value = e.target.value as ReportingType;
                                        if (!value) {
                                            setLocal(prev => ({ ...prev, [year]: value }));
                                            return;
                                        }
                                        setPendingSelection({ year, value });
                                    }}
                                    sx={{ fontSize: 13, minWidth: 100 }}
                                    MenuProps={{ sx: { zIndex: 1500 } }}
                                    disabled={isLocked}
                                >
                                    {REPORTING_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        );
                    })}
                </Box>

                <Typography fontSize={11} color="text.secondary" sx={{ px: 2, pt: 1, fontStyle: 'italic' }}>
                    Once a year's reporting cycle is saved, it can't be changed.
                </Typography>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid #eee' }}>
                    <Button onClick={onClose} size="small" sx={{ color: 'var(--color-text-muted)', textTransform: 'none', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onSave(local)}
                        size="small"
                        variant="contained"
                        disabled={!hasAnyEditableYear}
                        sx={{ background: 'var(--color-primary)', textTransform: 'none', fontWeight: 600, boxShadow: 'none', borderRadius: 1.5 }}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>
            <DeletePopup
                open={!!pendingSelection}
                zIndex={1500}
                message="Confirm GST Reporting Cycle"
                description="The GST cycle you choose will apply to the entire year. This setting can only be modified once. Are you sure you want to continue?"
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                onConfirm={() => {
                    if (!pendingSelection) return;
                    const { year, value } = pendingSelection;
                    setLocal(prev => ({ ...prev, [year]: value }));
                    setConfirmedYears(prev => new Set(prev).add(year));
                    setPendingSelection(null);
                }}
                onCancel={() => setPendingSelection(null)}
            />
        </>
    );
};

export default GSTCycleModal;
